// RBAC Diagnostics Component
// Use this to verify RBAC is working correctly

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  getUserPermissions, 
  getUserRoles, 
  hasPermission,
  PERMISSIONS 
} from '../../../utils/permissions';
import PermissionGuard from '../../Common/PermissionGuard';

function RBACDiagnostics() {
  const [diagnostics, setDiagnostics] = useState({
    localStorageData: null,
    roles: [],
    permissions: [],
    permissionOverrides: {},
    checks: {}
  });

  const runDiagnostics = () => {
    // Get raw localStorage data
    const userDataRaw = localStorage.getItem('userData');
    const userDataParsed = userDataRaw ? JSON.parse(userDataRaw) : null;

    // Get using utility functions
    const roles = getUserRoles();
    const permissions = getUserPermissions();

    // Test specific permissions
    const checks = {
      'HRM.EMPLOYEE.VIEW': hasPermission(PERMISSIONS.HRM_EMPLOYEE_VIEW),
      'HRM.EMPLOYEE.CREATE': hasPermission(PERMISSIONS.HRM_EMPLOYEE_CREATE),
      'HRM.EMPLOYEE.EDIT': hasPermission(PERMISSIONS.HRM_EMPLOYEE_EDIT),
      'HRM.EMPLOYEE.DELETE': hasPermission(PERMISSIONS.HRM_EMPLOYEE_DELETE),
      'SYSTEM.USERS.MANAGE': hasPermission(PERMISSIONS.SYSTEM_USERS_MANAGE),
    };

    setDiagnostics({
      localStorageData: userDataParsed,
      roles,
      permissions,
      permissionOverrides: userDataParsed?.permissionOverrides || {},
      checks
    });
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const getStatusIcon = (value) => {
    return value ? (
      <CheckCircleIcon color="success" />
    ) : (
      <CancelIcon color="error" />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">RBAC Diagnostics</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={runDiagnostics}
          >
            Refresh
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          This page shows your current RBAC configuration. If permissions are not working, 
          logout and login again to refresh your JWT token.
        </Alert>

        {/* localStorage Status */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          1. localStorage Status
        </Typography>
        {diagnostics.localStorageData ? (
          <Alert severity="success">
            ✅ userData found in localStorage
          </Alert>
        ) : (
          <Alert severity="error">
            ❌ userData NOT found in localStorage - Login integration may not be working
          </Alert>
        )}

        {/* Roles */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          2. Your Roles
        </Typography>
        {diagnostics.roles.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {diagnostics.roles.map(role => (
              <Chip key={role} label={role} color="primary" />
            ))}
          </Box>
        ) : (
          <Alert severity="warning">
            ⚠️ No roles found - Default EMPLOYEE role should be assigned
          </Alert>
        )}

        {/* Permissions Count */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          3. Your Permissions ({diagnostics.permissions.length})
        </Typography>
        {diagnostics.permissions.length > 0 ? (
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
            {diagnostics.permissions.map(perm => (
              <Typography key={perm} variant="body2" sx={{ fontFamily: 'monospace' }}>
                • {perm}
              </Typography>
            ))}
          </Box>
        ) : (
          <Alert severity="error">
            ❌ No permissions found - This means RBAC is not working
          </Alert>
        )}

        {/* Permission Overrides */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          4. Permission Overrides
        </Typography>
        {Object.keys(diagnostics.permissionOverrides).length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Permission</TableCell>
                <TableCell>Override Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(diagnostics.permissionOverrides).map(([perm, value]) => (
                <TableRow key={perm}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{perm}</TableCell>
                  <TableCell>
                    <Chip 
                      label={value ? 'ALLOW' : 'DENY'} 
                      color={value ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No permission overrides set
          </Typography>
        )}

        {/* Permission Checks */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          5. Permission Check Tests
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Permission</TableCell>
              <TableCell>Has Access</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(diagnostics.checks).map(([perm, hasAccess]) => (
              <TableRow key={perm}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{perm}</TableCell>
                <TableCell>{getStatusIcon(hasAccess)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />

        {/* Permission Guard Tests */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          6. Permission Guard Component Tests
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PermissionGuard permission={PERMISSIONS.HRM_EMPLOYEE_VIEW}>
            <Alert severity="success">
              ✅ HRM.EMPLOYEE.VIEW - You can see this
            </Alert>
          </PermissionGuard>

          <PermissionGuard 
            permission={PERMISSIONS.HRM_EMPLOYEE_CREATE}
            fallback={<Alert severity="error">❌ HRM.EMPLOYEE.CREATE - You cannot see this (no permission)</Alert>}
          >
            <Alert severity="success">
              ✅ HRM.EMPLOYEE.CREATE - You can see this
            </Alert>
          </PermissionGuard>

          <PermissionGuard 
            permission={PERMISSIONS.HRM_EMPLOYEE_DELETE}
            fallback={<Alert severity="error">❌ HRM.EMPLOYEE.DELETE - You cannot see this (no permission)</Alert>}
          >
            <Alert severity="success">
              ✅ HRM.EMPLOYEE.DELETE - You can see this
            </Alert>
          </PermissionGuard>

          <PermissionGuard 
            permission={PERMISSIONS.SYSTEM_USERS_MANAGE}
            fallback={<Alert severity="error">❌ SYSTEM.USERS.MANAGE - You cannot see this (no permission)</Alert>}
          >
            <Alert severity="success">
              ✅ SYSTEM.USERS.MANAGE - You can see this
            </Alert>
          </PermissionGuard>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Raw Data */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          7. Raw localStorage Data
        </Typography>
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          p: 2, 
          borderRadius: 1, 
          overflow: 'auto',
          maxHeight: 300
        }}>
          <pre style={{ margin: 0, fontSize: 12 }}>
            {JSON.stringify(diagnostics.localStorageData, null, 2)}
          </pre>
        </Box>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Important Notes:
          </Typography>
          <Typography variant="body2">
            • If you changed roles in RBAC Management, you MUST logout and login again
          </Typography>
          <Typography variant="body2">
            • JWT tokens contain roles/permissions - they don't update until you get a new token
          </Typography>
          <Typography variant="body2">
            • If localStorage is empty, the login integration is not working
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
}

export default RBACDiagnostics;
